import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { themeClasses, Button } from '@member-portal/ui';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import { useAuth } from '@/contexts/AuthContext';

interface DropdownOptions {
  agencyAffiliations: string[];
  referralSources: string[];
}

interface AdvisorSignupFormProps {
  sectionId?: string;
  showLoginLink?: boolean;
  showSupportInfo?: boolean;
  defaultReferredBy?: string;
  heading?: string;
}

function AdvisorSignupForm({
  sectionId = 'advisor-signup',
  showLoginLink = true,
  showSupportInfo = true,
  defaultReferredBy,
  heading = 'Advisor Registration',
}: AdvisorSignupFormProps) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    agency: '',
    agencyAffiliation: '',
    email: '',
    countryCode: 'US',
    phone: '',
    iataNumber: '',
    referredBy: defaultReferredBy || '',
    acceptedTerms: false,
  });

  const countries = getCountries().sort((a, b) => {
    if (a === 'US') return -1;
    if (b === 'US') return 1;
    return (en[a] || a).localeCompare(en[b] || b);
  });

  const formatPhoneDisplay = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const getFormattedPhone = () => {
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length === 0) return '';
    const callingCode = getCountryCallingCode(formData.countryCode as any);
    return `+${callingCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const [options, setOptions] = useState<DropdownOptions>({
    agencyAffiliations: [
      'American Express',
      'Ensemble',
      'Signature',
      'Virtuoso',
      'TravellerMade',
      'TravelSavers',
      'Internova Associated Agency/GlobalTravel Collection/Travel Leaders Group',
      'Travel Edge',
      'Carlson Wagonlit',
      'None',
      'Other',
    ],
    referralSources: [
      'Social Media',
      'Search Engine',
      'Email Campaign',
      'Travel Advisor',
      'Other',
    ],
  });
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`/api/fhxd-auth/options`);
        const data = await response.json();
        if (data.success) {
          setOptions(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'phone') {
      setFormData({ ...formData, phone: formatPhoneDisplay(value) });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }

    if (error) setError(null);
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        phone: getFormattedPhone(),
      };
      delete (submitData as { countryCode?: string }).countryCode;

      const response = await fetch(`/api/fhxd-auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        if (data.token && data.user) {
          login(data.token, data.user);
        }
        setSuccess(true);
      } else if (data.field) {
        setFieldErrors({ [data.field]: data.message });
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section
        id={sectionId}
        className="py-16 bg-theme-bg scroll-mt-[var(--fhxd-chrome-height)]"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${themeClasses.title.h3} mb-6`}>Registration Complete</h2>
          <p className={`${themeClasses.text.body} mb-8`}>
            Thank you for registering with Four Hundred by Design. We've sent a confirmation email
            to your inbox.
          </p>
          <Button variant="primary" onClick={() => navigate('/collection')}>
            Explore Collection
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section
      id={sectionId}
      className="py-16 bg-theme-bg scroll-mt-[var(--fhxd-chrome-height)]"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className={`${themeClasses.title.h3} text-center mb-4`}>{heading}</h2>
        {showLoginLink && (
          <p className={`${themeClasses.text.body} text-center mb-12 text-theme-text-muted`}>
            Already have an account?{' '}
            <Link to="/login" className="underline hover:text-theme-text transition-colors">
              Login here
            </Link>
          </p>
        )}
        {!showLoginLink && <div className="mb-12" />}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className={themeClasses.form.label}>
                First Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
                className={`${themeClasses.form.input} font-normal`}
              />
            </div>
            <div>
              <label htmlFor="lastName" className={themeClasses.form.label}>
                Last Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
                className={`${themeClasses.form.input} font-normal`}
              />
            </div>
          </div>

          <div>
            <label htmlFor="agency" className={themeClasses.form.label}>
              Agency <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="agency"
              name="agency"
              required
              value={formData.agency}
              onChange={handleChange}
              placeholder="Agency Name"
              className={`${themeClasses.form.input} font-normal`}
            />
          </div>

          <div>
            <label htmlFor="agencyAffiliation" className={themeClasses.form.label}>
              Agency Affiliation <span className="text-red-600">*</span>
            </label>
            <select
              id="agencyAffiliation"
              name="agencyAffiliation"
              required
              value={formData.agencyAffiliation}
              onChange={handleChange}
              className={`${themeClasses.form.select} font-normal`}
              disabled={optionsLoading}
            >
              <option value="">Select Agency Affiliation</option>
              {options.agencyAffiliations.map((affiliation) => (
                <option key={affiliation} value={affiliation}>
                  {affiliation}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className={themeClasses.form.label}>
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="Email Address"
                className={`${themeClasses.form.input} font-normal`}
              />
            </div>
            <div>
              <label htmlFor="phone" className={themeClasses.form.label}>
                Phone Number <span className="text-red-600">*</span>
              </label>
              <div className="flex">
                <select
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className={`${themeClasses.form.select} font-normal !w-20 flex-shrink-0 border-r-0 px-2`}
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      +{getCountryCallingCode(country)}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                  maxLength={14}
                  className={`${themeClasses.form.input} font-normal flex-1`}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="iataNumber" className={themeClasses.form.label}>
                IATA # <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="iataNumber"
                name="iataNumber"
                required
                maxLength={11}
                value={formData.iataNumber}
                onChange={handleChange}
                placeholder="IATA Number"
                className={`${themeClasses.form.input} font-normal ${
                  fieldErrors.iataNumber ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
              />
              {fieldErrors.iataNumber && (
                <p className="mt-2 text-sm font-body font-medium text-red-500">{fieldErrors.iataNumber}</p>
              )}
            </div>
            <div>
              <label htmlFor="referredBy" className={themeClasses.form.label}>
                Referred By <span className="text-red-600">*</span>
              </label>
              <select
                id="referredBy"
                name="referredBy"
                required
                value={formData.referredBy}
                onChange={handleChange}
                className={`${themeClasses.form.select} font-normal`}
                disabled={optionsLoading}
              >
                <option value="">Select Referred By</option>
                {options.referralSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={handleChange}
                required
                className="mt-1 h-4 w-4 border-theme-border text-black focus:ring-black"
              />
              <span className={`${themeClasses.text.mutedSmall}`}>
                I have read and agree with the{' '}
                <Link to="/terms" target="_blank" className="underline hover:text-theme-text transition-colors">
                  Terms & Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="underline hover:text-theme-text transition-colors">
                  Privacy Policy
                </Link>
                . <span className="text-red-600">*</span>
              </span>
            </label>
          </div>

          <div className="text-center pt-6">
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !formData.acceptedTerms}
              className="min-w-[200px]"
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </div>
        </form>

        {showSupportInfo && (
          <div className="mt-12 pt-12 border-t border-theme-border text-center">
            <p className={themeClasses.text.body}>
              Have questions?{' '}
              <Link to="/contact" className="text-theme-text hover:text-theme-text-muted transition-colors underline">
                Contact us
              </Link>{' '}
              or email{' '}
              <a
                href="mailto:info@fourhundredbydesign.com"
                className="text-theme-text hover:text-theme-text-muted transition-colors underline"
              >
                info@fourhundredbydesign.com
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export default AdvisorSignupForm;
